import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { ContextOperation } from "../../../context/OperationContext";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useSnackbar from "../../../hooks/useSnackbar";
import useToggle from "../../../hooks/useToggle";
import { saveFileFromResponse } from "../../../utils";
import { nameof } from "../../common/ObjectUtils";
import DamageFormService from "../service/DamageFormService";
import { DamageFormTypes } from "../Types";
import Dialogs from "./dialogs";
import { ContextDamageForm } from "../DamageFormContext";
import { useHasScope } from "../../../../../config/globalConfig";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import useDamageFormActions from "../service/DamageFormActions";
import EmptyStateGrid from "../../common/EmptyStateGrid";
import AgGridOperationTable from "../../common/AgGridOperationTable";
import PhotoGallery, { TypeImages } from "../../common/PhotoGallery";
import { json } from "stream/consumers";

const AttachmentsTable = () => {
  const hasScope = useHasScope;

  const { tenant, setLoading } = useContext(ContextOperation);
  const damageFormContext = useContext(ContextDamageForm);
  const { globalData } = useContext(GlobalContext);

  const t = useOperationTranslation();

  const snackBar = useSnackbar();

  const [openCreate, toggleCreate] = useToggle();
  const [openEdit, toggleEdit] = useToggle();
  const actions = useDamageFormActions();

  const [photos, setPhotos] = useState<TypeImages[]>([]);
  const [isOpenPhotoGallery, togglePhotoGallery] = useToggle();

  //const [dataSource, setDataSource] = useState<DamageFormTypes.IAttachments[]>([]);
  const [selectedAttachment, setSelectedAttachment] = useState<
    DamageFormTypes.IAttachments | undefined
  >();

  const { id } = useParams();

  const getAttachments = () => {
    if (!id) return;
    DamageFormService.details.generalInfo.attachments.get
      .byId(id)
      .then((response) => {
        //setDataSource(response);
        damageFormContext.setAttachments(response);
		damageFormContext.setPhotos(response.filter(
          (val) => val.attachmentType === "Fotografia"
        ));

      });
  };
  
  const openGallery = () => {    

    if (damageFormContext.photos != null) {

      setLoading(true);

      Promise.all(damageFormContext.photos.map((photo) => (DamageFormService.details.generalInfo.attachments.get.blobByIdForGallery(photo.id.toString(), photo.type))))
      .then(
        response => {
          setPhotos(response.map((res) => ({ base64: res })));    
          togglePhotoGallery();
          console.log("PHOTOS: " + JSON.stringify(photos));
          setLoading(false);              
      }
      ).catch((error: any) => {
        setLoading(false);
        snackBar.show("error", t("damageForm.details.generalInfo.attachments.snackbar.file.error"));
      });
      

    }
  };

    const onClosePhotoGallery = () => {
        togglePhotoGallery();
        setPhotos([]);
    }


  useEffect(() => {
    if (openCreate || openEdit) return;

    getAttachments();
    actions.getTask1();
  }, [openCreate, openEdit]);

  const docTypes = useMemo(() => {
    return {
      documents: damageFormContext.attachments?.filter(
        (val) => val.attachmentType !== "Fotografia"
      ).length,
      photos: damageFormContext.attachments?.filter(
        (val) => val.attachmentType === "Fotografia"
      ).length,
    };
  }, [damageFormContext.attachments]);

  const onEditFile = (attachment: DamageFormTypes.IAttachments | undefined) => {
    setSelectedAttachment(attachment);
    toggleEdit();
  };

  const checkAddAttachmentEnabled = () => {

    if (damageFormContext.task1?.formManagementLms != null && damageFormContext.task1?.formManagementLms?.id != null) {

        return (hasScope('SCOPE_CREATE_DAMAGE_ATTACHMENT_BY_DMS')) && (globalData?.user?.mobilityStores.filter(
            ({ sapId }: any) => sapId === damageFormContext.task1?.formManagementLms?.id
          ).length > 0);

    } else if (damageFormContext.generalInfo?.createUsername != null) {
      return (hasScope('SCOPE_CREATE_DAMAGE_ATTACHMENT_BY_DMS')) && damageFormContext.generalInfo?.createUsername === globalData?.user?.username;
    } else {
      return false;
    }
}

const checkDownloadAttachmentEnabled = () => {

  if (damageFormContext.task1?.formManagementLms != null && damageFormContext.task1?.formManagementLms?.id != null) {
      return (hasScope('SCOPE_DOWNLOAD_DAMAGE_ATTACHMENT_BY_DMS')) && (globalData?.user?.mobilityStores.filter(
          ({ sapId }: any) => sapId === damageFormContext.task1?.formManagementLms?.id
        ).length > 0);

  } else if (damageFormContext.generalInfo?.createUsername != null) {    
    return (hasScope('SCOPE_DOWNLOAD_DAMAGE_ATTACHMENT_BY_DMS')) && damageFormContext.generalInfo?.createUsername === globalData?.user?.username;
  } else {   
    return false;
  }
}

  const onDeleteFile = (
    attachmentId: string | undefined,
    type: string | undefined
  ) => {
    if (!attachmentId) {
      snackBar.show(
        "warning",
        t("damageForm.details.generalInfo.attachments.snackbar.delete.noId")
      );
      return;
    }

    setLoading(true);
    DamageFormService.details.generalInfo.attachments
      .delete(tenant, attachmentId, type)
      .then(
        () => {
          getAttachments();
          snackBar.show(
            "success",
            t(
              "damageForm.details.generalInfo.attachments.snackbar.delete.success"
            )
          );
          setLoading(false);
        },
        () => {
          snackBar.show(
            "error",
            t(
              "damageForm.details.generalInfo.attachments.snackbar.delete.error"
            )
          );
          setLoading(false);
        }
      );
  };

  const onDownloadZip = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.generalInfo.attachments.get.zipById(id).then(
      (response) => {
        saveFileFromResponse(response);
        setLoading(false);
      },
      () => {
        snackBar.show(
          "error",
          t("damageForm.details.generalInfo.attachments.snackbar.zip.error")
        );
        setLoading(false);
      }
    );
  };

  const onDownloadAttachment = (
    attachmentId: number | undefined,
    type: string | undefined
  ) => {
    if (!attachmentId) return;

    setLoading(true);
    DamageFormService.details.generalInfo.attachments.get
      .blobById(attachmentId.toString(), type)
      .then(
        (response) => {
          saveFileFromResponse(response);
          setLoading(false);
        },
        () => {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.attachments.snackbar.file.error")
          );
          setLoading(false);
        }
      );
  };

  const fieldFunction = () => {
    if (
      hasScope("SCOPE_UPDATE_DAMAGE_ATTACHMENT") ||
      hasScope("SCOPE_DELETE_DAMAGE_ATTACHMENT") ||
      hasScope("SCOPE_CREATE_DAMAGE_ATTACHMENT")
    )
      return "_actions";
    else return "";
  };

  const columns: ColDef[] = [
    {
      field: nameof<DamageFormTypes.IAttachments>("filename"),
      headerName: t(
        "damageForm.details.generalInfo.attachments.table.headers.fileName"
      ),
      flex: 1,
      cellRenderer: (
        params: ICellRendererParams<DamageFormTypes.IAttachments>
      ) => {

        if (hasScope("SCOPE_DOWNLOAD_DAMAGE_ATTACHMENT") || checkDownloadAttachmentEnabled()) {

          return (
        
            <Mui.Link
              onClick={() =>
                onDownloadAttachment(params.data?.id, params.data?.type)
              }
            >
              {params.value}
            </Mui.Link>        
          
          );
        } else {
          return (        
           <span> {params.value} </span>
          
          );
        }
      },
    },
    {
      field: nameof<DamageFormTypes.IAttachments>("attachmentType"),
      headerName: t(
        "damageForm.details.generalInfo.attachments.table.headers.attachmentType"
      ),
      flex: 1,
    },
    {
      field: nameof<DamageFormTypes.IAttachments>("documentTypeLabel"),
      headerName: t(
        "damageForm.details.generalInfo.attachments.table.headers.documentType"
      ),
      flex: 1,
    },
    {
      field: nameof<DamageFormTypes.IAttachments>("updateDate"),
      headerName: t(
        "damageForm.details.generalInfo.attachments.table.headers.updateDate"
      ),
      flex: 1,
      cellRenderer: (
        params: ICellRendererParams<DamageFormTypes.IAttachments>
      ) => {
        return dayjs(params.value).format("DD/MM/YYYY HH:mm:ss");
      },
    },
    {
      field: fieldFunction(),
      headerName: "",
      maxWidth: 100,
      minWidth: 100,
      cellRenderer: (
        params: ICellRendererParams<DamageFormTypes.IAttachments>
      ) => {
        return (
          <>
            {hasScope("SCOPE_UPDATE_DAMAGE_ATTACHMENT") && (
              <Mui.Button
                variant="text"
                size="icon"
                onClick={() => onEditFile(params.data)}
                disabled={damageFormContext.generalInfo?.migrated || !damageFormContext.generalInfo?.open}
              >
                <MuiIcons.Edit />
              </Mui.Button>
            )}

            {hasScope("SCOPE_DELETE_DAMAGE_ATTACHMENT") && (
              <Mui.Button
                variant="text"
                size="icon"
                onClick={() =>
                  onDeleteFile(params.data?.id.toString(), params.data?.type)
                }
                disabled={damageFormContext.generalInfo?.migrated  || !damageFormContext.generalInfo?.open}
              >
                <MuiIcons.Delete />
              </Mui.Button>
            )}
          </>
        );
      },
    },
  ];

  const message = useMemo<string>(() => {
    let text = "";

    if (docTypes.documents) {
      text = `${t(
        "damageForm.details.generalInfo.attachments.lblDocuments"
      )}: ${docTypes.documents}`;
    }

    if (docTypes.documents && docTypes.photos) {
      text = `${text} | `;
    }

    if (docTypes.photos) {
      text = `${text} ${t(
        "damageForm.details.generalInfo.attachments.lblPhotos"
      )}: ${docTypes.photos}`;
    }

    return text;
  }, [docTypes]);

  return (
    <>
      <Mui.Accordion>
        <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />}>
          {t("damageForm.details.generalInfo.attachments.title")}
        </Mui.AccordionSummary>

        <Mui.AccordionDetails>
          <Mui.Card>
            <Mui.CardContent>
              <Mui.Grid container spacing={1} mb={2}>
                <Mui.Grid item xs={12} sm="auto">
                  <Mui.Typography>{message}</Mui.Typography>
                </Mui.Grid>

                <Mui.Grid item xs="auto" ml="auto">
                  {(hasScope("SCOPE_DOWNLOAD_DAMAGE_ATTACHMENT") || checkDownloadAttachmentEnabled()) &&
                  <Mui.Button
                    variant="contained"
                    startIcon={<MuiIcons.Download />}
                    onClick={onDownloadZip}
                  >
                    <Mui.Typography
                      color={"accent"}
                      fontWeight={700}
                      fontSize={"13px"}
                    >
                      {t(
                        "damageForm.details.generalInfo.attachments.btnDownloadZip"
                      )}
                    </Mui.Typography>
                  </Mui.Button>
                }
                </Mui.Grid>
				
				<Mui.Grid item xs="auto">
                 
                  <Mui.Button
                    variant="contained"
                    startIcon={<MuiIcons.Image />}
                    onClick={openGallery}
                    disabled={damageFormContext.generalInfo?.migrated || damageFormContext.photos == null || damageFormContext.photos.length == 0}
             
                  >
                    <Mui.Typography
                      color={"accent"}
                      fontWeight={700}
                      fontSize={"13px"}
                    >
                      {t(
                        "damageForm.details.generalInfo.attachments.viewGallery"
                      )}
                    </Mui.Typography>
                  </Mui.Button>
                </Mui.Grid>

                <Mui.Grid item xs="auto">
                  {(hasScope("SCOPE_CREATE_DAMAGE_ATTACHMENT") || checkAddAttachmentEnabled()) && (
                    <Mui.Button
                      variant="text"
                      size="icon"
                      onClick={toggleCreate}
                      disabled={damageFormContext.generalInfo?.migrated  || !damageFormContext.generalInfo?.open}
                    >
                      <MuiIcons.Add />
                    </Mui.Button>
                  )}
                </Mui.Grid>
              </Mui.Grid>

              <AgGridOperationTable
                columnDefs={columns}
                rowData={damageFormContext.attachments}
                rowModelType="clientSide"
                totalElements={damageFormContext?.attachments?.length}
              />

            </Mui.CardContent>
          </Mui.Card>
        </Mui.AccordionDetails>
      </Mui.Accordion>

      <Dialogs.CreateAttachment open={openCreate} onClose={toggleCreate} />

      {openEdit && selectedAttachment && (
        <Dialogs.EditAttachment
          open={openEdit}
          onClose={toggleEdit}
          setSelectedAttachment={setSelectedAttachment}
          attachment={selectedAttachment}
        />
      )}
	  <PhotoGallery
                images={photos}
                onClose={onClosePhotoGallery}
                open={isOpenPhotoGallery}
      />

    </>
  );
};

export default AttachmentsTable;
